import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Upload, Trash2, Image as ImageIcon, Eye, Sparkles, Loader2 } from 'lucide-react';

/**
 * Agent White-Label Branding Card.
 *
 * Lets agents upload a logo + company details that get stamped onto
 * E-Tickets they generate for their customers. Silently falls back to
 * ACF defaults when fields are left blank (no aggressive nudges).
 *
 * Mounted inside AgentProfile as a stand-alone card so the existing
 * profile editor stays untouched.
 */
const FIELD_DEFS = [
  { key: 'company_name', label: 'Company Name (shown on ticket)', placeholder: 'Your Travel Agency Pvt. Ltd.', maxLength: 120 },
  { key: 'address', label: 'Office Address', placeholder: '101, Main Street, City, State PIN', maxLength: 240 },
  { key: 'phone', label: 'Support Phone', placeholder: '+91 98765 43210', maxLength: 32 },
  { key: 'email', label: 'Support Email', placeholder: 'support@youragency.com', maxLength: 120 },
  { key: 'website', label: 'Website', placeholder: 'www.youragency.com', maxLength: 120 },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AgentBrandingCard = () => {
  const [branding, setBranding] = useState({});
  const [logoPreviewBust, setLogoPreviewBust] = useState(0);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loaded, setLoaded] = useState(false);
  const fileInputRef = useRef(null);

  const fetchBranding = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/agent/branding`, { withCredentials: true });
      setBranding(r.data || {});
      setFormData({
        company_name: r.data.company_name || '',
        address: r.data.address || '',
        phone: r.data.phone || '',
        email: r.data.email || '',
        website: r.data.website || '',
      });
    } catch (e) {
      // Silent — card just shows empty state on load failure
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/agent/branding`, { withCredentials: true });
        if (cancelled) return;
        setBranding(r.data || {});
        setFormData({
          company_name: r.data.company_name || '',
          address: r.data.address || '',
          phone: r.data.phone || '',
          email: r.data.email || '',
          website: r.data.website || '',
        });
      } catch (e) {
        // ignore
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => fd.append(k, v || ''));
      const r = await axios.put(`${API_URL}/api/agent/branding`, fd, { withCredentials: true });
      setBranding(r.data);
      setSuccess('Branding saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (ex) {
      setError(ex.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(''); setSuccess(''); setUploading(true);
    try {
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo must be under 2 MB');
        return;
      }
      const fd = new FormData();
      fd.append('logo', file);
      await axios.post(`${API_URL}/api/agent/branding/logo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess('Logo uploaded');
      setLogoPreviewBust((n) => n + 1);
      await fetchBranding();
      setTimeout(() => setSuccess(''), 3000);
    } catch (ex) {
      setError(ex.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onLogoDelete = async () => {
    if (!window.confirm('Remove your logo? Tickets will revert to the ACF default.')) return;
    setError(''); setSuccess(''); setDeletingLogo(true);
    try {
      await axios.delete(`${API_URL}/api/agent/branding/logo`, { withCredentials: true });
      setSuccess('Logo removed');
      await fetchBranding();
      setTimeout(() => setSuccess(''), 3000);
    } catch (ex) {
      setError(ex.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingLogo(false);
    }
  };

  // Logo URL — we go through a tiny client-side bust query string so
  // the browser doesn't cache a stale image after re-upload.
  const logoUrl = branding.has_logo
    ? `${API_URL}/api/agent/branding/logo?v=${logoPreviewBust}`
    : '';

  if (!loaded) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <Loader2 className="animate-spin text-gray-400" size={20} />
      </div>
    );
  }

  return (
    <div
      data-testid="agent-branding-card"
      className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 space-y-5"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="text-blue-600 mt-1" size={20} />
        <div>
          <h2 className="text-lg font-semibold text-gray-800">White-Label E-Tickets</h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Stamp your own logo and contact details on the e-tickets you generate. Leave blank to use the ACF default.
          </p>
        </div>
      </div>

      {error && <div data-testid="branding-error" className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}
      {success && <div data-testid="branding-success" className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">{success}</div>}

      {/* Logo block */}
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div
          data-testid="branding-logo-preview"
          className="w-32 h-32 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0"
        >
          {branding.has_logo ? (
            <img src={logoUrl} alt="Agency logo" className="max-w-full max-h-full object-contain" />
          ) : (
            <div className="text-center text-gray-400">
              <ImageIcon size={28} className="mx-auto" />
              <p className="text-xs mt-1">No logo</p>
            </div>
          )}
        </div>
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            onChange={onLogoChange}
            className="hidden"
            data-testid="branding-logo-input"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="branding-logo-upload-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {branding.has_logo ? 'Replace Logo' : 'Upload Logo'}
            </button>
            {branding.has_logo && (
              <button
                type="button"
                onClick={onLogoDelete}
                disabled={deletingLogo}
                data-testid="branding-logo-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
              >
                {deletingLogo ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Remove
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            PNG or JPG · max 2 MB · auto-resized to fit a 400×400 px box. Transparent PNG recommended.
          </p>
        </div>
      </div>

      {/* Text fields */}
      <form onSubmit={onSave} className="space-y-4">
        {FIELD_DEFS.map(({ key, label, placeholder, maxLength }) => (
          <div key={key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            <input
              type={key === 'email' ? 'email' : 'text'}
              value={formData[key] || ''}
              onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
              placeholder={placeholder}
              maxLength={maxLength}
              data-testid={`branding-${key}-input`}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
        ))}
        <div className="flex flex-wrap gap-3 pt-1">
          <button
            type="submit"
            disabled={saving}
            data-testid="branding-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg font-medium transition-colors"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Save Branding
          </button>
          <a
            href={`${API_URL}/api/agent/branding`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium text-sm"
            data-testid="branding-view-json-btn"
          >
            <Eye size={16} />
            Live values (JSON)
          </a>
        </div>
      </form>
    </div>
  );
};

export default AgentBrandingCard;
